import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  isItemAvailable,
  isItemWaitingForSale,
} from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import type { FlattenedPortfolioItem } from "@/types";
import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";

/**
 * Shared cache of flattened portfolio items for the active company. Holds
 * no selection state — callers that show a subset pass it to the table as
 * `items` —
 * see {@link usePortfolioSelection} for per-instance selection.
 *
 * `allItems` is a `shallowRef` to avoid making 10k+ items deeply reactive.
 * Consumers fetch via {@link fetchItems} or {@link refetchItems}; views
 * that own custom fetching can register their loader via
 * {@link registerRefetch} so refetches stay consistent.
 */
export const usePortfolioItemsStore = defineStore("portfolioItemsStore", () => {
  // shallowRef to avoid making 10,000+ objects reactive (performance)
  const allItems = shallowRef<FlattenedPortfolioItem[]>([]);
  const isLoading = ref(false);
  const currentCompanyId = ref<number | null>(null);
  const refetchAction = shallowRef<(() => Promise<void>) | null>(null);
  const sourceHasMultipleCompanies = ref(false);

  const itemsMap = computed(() => {
    const map = new Map<number, FlattenedPortfolioItem>();
    for (const item of allItems.value) {
      map.set(item.id, item);
    }
    return map;
  });

  const availableItems = computed((): FlattenedPortfolioItem[] => {
    return allItems.value.filter(isItemAvailable);
  });

  const totalAvailableGOs = computed((): number => {
    const availableForSale = allItems.value.filter(
      (item) => isItemAvailable(item) || isItemWaitingForSale(item)
    );
    return availableForSale.reduce((total, item) => total + item.count, 0);
  });

  function setAllItems(items: FlattenedPortfolioItem[]): void {
    allItems.value = items;
  }

  function registerRefetch(action: () => Promise<void>): void {
    refetchAction.value = action;
  }

  async function fetchItems(companyId: number): Promise<void> {
    isLoading.value = true;
    currentCompanyId.value = companyId;
    refetchAction.value = null;
    sourceHasMultipleCompanies.value = false;
    try {
      const response = await goSolidApi.get(
        `/api/companies/${companyId}/flattened_portfolio_items`
      );
      const items = response.data.portfolio_items ?? [];
      setAllItems(items);
    } catch (error) {
      setAllItems([]);
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  async function refetchItems(): Promise<void> {
    if (refetchAction.value !== null) {
      await refetchAction.value();
      return;
    }

    if (currentCompanyId.value === null) {
      return;
    }
    await fetchItems(currentCompanyId.value);
  }

  function setSourceHasMultipleCompanies(
    items: FlattenedPortfolioItem[]
  ): void {
    if (items.length < 2) {
      sourceHasMultipleCompanies.value = false;
      return;
    }
    const firstId = items[0].company_id;
    sourceHasMultipleCompanies.value = items.some(
      (i) => i.company_id !== firstId
    );
  }

  return {
    allItems,
    isLoading,
    sourceHasMultipleCompanies,
    itemsMap,
    availableItems,
    totalAvailableGOs,
    fetchItems,
    refetchItems,
    setAllItems,
    registerRefetch,
    setSourceHasMultipleCompanies,
  };
});
